import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import getApiUrl from '../../../config/api';
import { toast } from 'react-toastify';

const DailyDistributionModal = ({
    isOpen,
    onClose,
    selectedDate,
    selectedFirm,
    salaryData,
    getDailyRate,
    formatCurrency,
    onDataSaved,
    currentFirmData
}) => {
    const [distributionData, setDistributionData] = useState(null);
    const [employeeAttendance, setEmployeeAttendance] = useState({});
    const [manualProductionAmounts, setManualProductionAmounts] = useState({}); // Store manually entered production amounts
    const [paymentAmounts, setPaymentAmounts] = useState({}); // Store payment amounts to record as employee transactions
    const [loading, setLoading] = useState(false);
    const [distributionMode, setDistributionMode] = useState('attendance'); // 'attendance' (График), 'production' (ОБЪЕМНЫЙ), or 'payment' (Оплата)
    const [employeeFilter, setEmployeeFilter] = useState('all'); // 'all', 'fixed', 'volume'

    useEffect(() => {
        if (isOpen && selectedDate && selectedFirm) {
            loadDistributionData();
        }
    }, [isOpen, selectedDate, selectedFirm]);

    const loadDistributionData = async () => {
        setLoading(true);
        
        try {
            // Load daily distribution data for this date
            const distributionResponse = await axios.get(`${getApiUrl()}/daily-distributions`, {
                params: { 
                    firm_id: selectedFirm,
                    date: selectedDate
                },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            // Load production earnings total for this date
            const productionResponse = await axios.get(`${getApiUrl()}/production-earnings/total-for-date`, {
                params: { 
                    firm_id: selectedFirm,
                    date: selectedDate
                },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            // Load daily distribution items (employee attendance/amounts)
            const itemsResponse = await axios.get(`${getApiUrl()}/daily-distribution-items/by-firm-and-date`, {
                params: { 
                    firm_id: selectedFirm,
                    date: selectedDate
                },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            setDistributionData({
                distribution: itemsResponse.data?.data?.daily_distribution || itemsResponse.data?.daily_distribution || null,
                productionEarnings: productionResponse.data?.data?.total_earnings || 0,
                items: itemsResponse.data?.data?.items || itemsResponse.data?.items || []
            });

            // Initialize attendance data from existing items
            const attendance = {};
            const productionAmounts = {};
            
            // Get items from response - handle different possible response structures
            // API returns: { success: true, data: { items: [...], daily_distribution: {...}, employees: [...] } }
            // So items are at: itemsResponse.data.items (not itemsResponse.data.data.items)
            const items = itemsResponse.data?.items || 
                         itemsResponse.data?.data?.items || 
                         (Array.isArray(itemsResponse.data) ? itemsResponse.data : []);
            
            console.log('Loading distribution items. Response structure:', {
                fullResponse: itemsResponse.data,
                itemsFromData: itemsResponse.data?.data?.items,
                itemsFromRoot: itemsResponse.data?.items,
                itemsArray: items
            });
            
            if (items && Array.isArray(items) && items.length > 0) {
                console.log('📊 ОБЪЕМНЫЙ mode - Loading items from API. Total items:', items.length);
                items.forEach(item => {
                    console.log('📊 ОБЪЕМНЫЙ mode - Processing item from API:', {
                        employee_id: item.employee_id,
                        production_amount: item.production_amount,
                        production_amount_type: typeof item.production_amount,
                        attendance_amount: item.attendance_amount,
                        distributed_amount: item.distributed_amount,
                        fullItem: item
                    });
                    
                    if (item && item.employee_id) {
                        // Use string key to ensure consistency with employee.id
                        const employeeIdKey = item.employee_id?.toString();
                        if (!employeeIdKey) {
                            console.warn('📊 ОБЪЕМНЫЙ mode - Skipping item with invalid employee_id:', item);
                            return;
                        }
                        
                        // Mark employee as present if they have any distribution (attendance or production)
                        const attendanceAmt = parseFloat(item.attendance_amount) || 0;
                        const productionAmt = parseFloat(item.production_amount) || 0;
                        const distributedAmt = parseFloat(item.distributed_amount) || 0;
                        const hasDistribution = attendanceAmt > 0 || productionAmt > 0 || distributedAmt > 0;
                        
                        if (hasDistribution) {
                            attendance[employeeIdKey] = {
                                isPresent: true,
                                shiftMultiplier: item.attendance_status || '1.0',
                                distributedAmount: distributedAmt
                            };
                        }
                        
                        // 📊 ОБЪЕМНЫЙ mode: Load production_amount from API response (no fallback values)
                        // Only use the actual production_amount value from the database
                        if (item.production_amount !== undefined && item.production_amount !== null) {
                            // Handle both string "0.00" and number 0
                            const prodAmount = parseFloat(item.production_amount);
                            if (!isNaN(prodAmount)) {
                                // Load the production amount (even if 0) to show saved values
                                productionAmounts[employeeIdKey] = prodAmount;
                                console.log(`📊 ОБЪЕМНЫЙ mode - ✓ Loaded production amount for employee ${employeeIdKey}: ${prodAmount} (original: ${item.production_amount}, type: ${typeof item.production_amount})`);
                            } else {
                                console.warn(`📊 ОБЪЕМНЫЙ mode - ✗ Invalid production_amount for employee ${employeeIdKey}: ${item.production_amount}`);
                            }
                        } else {
                            console.log(`📊 ОБЪЕМНЫЙ mode - ✗ No production_amount field in API response for employee ${employeeIdKey}`);
                        }
                    }
                });
            } else {
                console.log('📊 ОБЪЕМНЫЙ mode - No items found in response or items is not an array. Items:', items);
            }
            
            console.log('📊 ОБЪЕМНЫЙ mode - Final state after loading:', {
                attendance,
                productionAmounts,
                productionAmountsCount: Object.keys(productionAmounts).length,
                productionAmountsKeys: Object.keys(productionAmounts),
                productionAmountsValues: Object.values(productionAmounts),
                productionAmountsDetails: Object.entries(productionAmounts).map(([key, value]) => ({
                    employeeId: key,
                    productionAmount: value
                }))
            });
            
            // Set state with data from API response
            setEmployeeAttendance(attendance);
            setManualProductionAmounts(productionAmounts);
            
            // Log after state update to verify
            setTimeout(() => {
                console.log('📊 ОБЪЕМНЫЙ mode - State updated. manualProductionAmounts should now contain:', productionAmounts);
            }, 100);

        } catch (error) {
            console.error('Error loading distribution data:', error);
            toast.error('Ошибка загрузки данных распределения');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDistribution = async () => {
        try {
            if (!distributionData?.distribution?.id) {
                toast.error('Нет данных для сохранения');
                return;
            }
            setLoading(true);
            
            // Prepare data for API request
            const itemsToSave = [];
            
            if (distributionMode === 'attendance') {
                // График mode: Process each employee's attendance data
                // Only send attendance_amount, don't send production_amount to preserve existing values
                Object.entries(employeeAttendance).forEach(([employeeIdKey, attendance]) => {
                    if (attendance.isPresent) {
                        // Find employee by matching id (handle both string and number)
                        const employee = salaryData?.find(emp => 
                            emp.id?.toString() === employeeIdKey || 
                            emp.id === parseInt(employeeIdKey) ||
                            emp.id === employeeIdKey
                        );
                        if (employee) {
                            const dailyRate = getDailyRate(employee);
                            const distributedAmount = Math.round(dailyRate * parseFloat(attendance.shiftMultiplier || '1.0'));
                            
                            itemsToSave.push({
                                employee_id: parseInt(employeeIdKey),
                                attendance_status: attendance.shiftMultiplier || '1.0',
                                attendance_amount: distributedAmount
                                // Don't send production_amount - backend will preserve existing value
                                // distributed_amount will be calculated automatically as attendance_amount + production_amount
                            });
                        }
                    }
                });
            } else {
                // 📊 ОБЪЕМНЫЙ mode: Process employees with manual production amounts
                // Only send production_amount, don't send attendance_amount to preserve existing values
                console.log('📊 ОБЪЕМНЫЙ mode - Saving production amounts. manualProductionAmounts:', manualProductionAmounts);
                Object.entries(manualProductionAmounts).forEach(([employeeIdKey, productionAmount]) => {
                    console.log(`📊 ОБЪЕМНЫЙ mode - Processing employee ${employeeIdKey} with production amount:`, productionAmount);
                    
                    // Parse the production amount
                    const prodAmount = parseFloat(productionAmount) || 0;
                    
                    // Only save if production amount > 0 (save for any employee with entered amount, regardless of checkbox)
                    if (prodAmount > 0) {
                        // Find employee by matching id (handle both string and number)
                        const employee = salaryData?.find(emp => 
                            emp.id?.toString() === employeeIdKey || 
                            emp.id === parseInt(employeeIdKey) ||
                            emp.id === employeeIdKey
                        );
                        if (employee) {
                            const itemToSave = {
                                employee_id: parseInt(employeeIdKey),
                                attendance_status: '1.0',
                                production_amount: Math.round(prodAmount) // Save the production amount
                                // Don't send attendance_amount - backend will preserve existing value
                                // distributed_amount will be calculated automatically as attendance_amount + production_amount
                            };
                            console.log('📊 ОБЪЕМНЫЙ mode - Adding item to save:', itemToSave);
                            itemsToSave.push(itemToSave);
                        } else {
                            console.warn(`📊 ОБЪЕМНЫЙ mode - Employee not found for id: ${employeeIdKey}`);
                        }
                    } else {
                        console.log(`📊 ОБЪЕМНЫЙ mode - Skipping employee ${employeeIdKey} (production amount is 0 or invalid)`);
                    }
                });
                console.log('📊 ОБЪЕМНЫЙ mode - Total items to save:', itemsToSave.length, itemsToSave);
            }
            
            if (itemsToSave.length === 0) {
                toast.warning('Выберите хотя бы одного сотрудника для распределения');
                return;
            }
            
            console.log('📊 ОБЪЕМНЫЙ mode - Sending save request:', {
                firm_id: selectedFirm,
                date: selectedDate,
                items: itemsToSave,
                distributionMode: distributionMode,
                itemsCount: itemsToSave.length,
                itemsDetails: itemsToSave.map(item => ({
                    employee_id: item.employee_id,
                    attendance_amount: item.attendance_amount,
                    production_amount: item.production_amount
                }))
            });
            
            // Make API request to save distribution items
            const response = await axios.post(`${getApiUrl()}/daily-distribution-items/bulk`, {
                firm_id: selectedFirm,
                date: selectedDate,
                items: itemsToSave
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            console.log('📊 ОБЪЕМНЫЙ mode - Save response:', {
                success: response.data?.success,
                message: response.data?.message,
                data: response.data?.data
            });
            
            console.log('Save response:', response.data);
            
            if (response.data.success) {
                toast.success('Распределение сохранено');
                // Call the callback to refresh data
                if (onDataSaved) {
                    onDataSaved();
                }
                onClose();
            } else {
                toast.error('Ошибка сохранения распределения');
            }
            
        } catch (error) {
            console.error('Error saving distribution:', error);
            toast.error('Ошибка сохранения распределения');
        } finally {
            setLoading(false);
        }
    };

    const updateEmployeeAttendance = (employeeId, updates) => {
        if (!employeeId || !updates) return;
        
        console.log('Updating attendance for employee:', employeeId, 'with updates:', updates);
        
        // Ensure consistent key type (string)
        const employeeIdKey = employeeId?.toString();
        setEmployeeAttendance(prev => {
            const newState = {
                ...prev,
                [employeeIdKey]: {
                    ...prev[employeeIdKey],
                    ...updates
                }
            };
            
            console.log('New attendance state:', newState);
            return newState;
        });
    };

    const updateManualProductionAmount = (employeeId, amount) => {
        if (!employeeId) return;
        
        const numericAmount = parseFloat(amount) || 0;
        // Ensure consistent key type (string)
        const employeeIdKey = employeeId?.toString();
        setManualProductionAmounts(prev => ({
            ...prev,
            [employeeIdKey]: numericAmount
        }));
    };

    const updatePaymentAmount = (employeeId, amount) => {
        if (!employeeId) return;
        
        const numericAmount = parseFloat(amount) || 0;
        const employeeIdKey = employeeId?.toString();
        setPaymentAmounts(prev => ({
            ...prev,
            [employeeIdKey]: numericAmount
        }));
    };

    const calculateTotalPayments = () => {
        return Object.values(paymentAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
    };

    const handleSavePayments = async () => {
        try {
            setLoading(true);
            
            const paymentsToSave = Object.entries(paymentAmounts)
                .filter(([_, amount]) => parseFloat(amount) > 0)
                .map(([employeeIdKey, amount]) => ({
                    employee_id: parseInt(employeeIdKey),
                    payment_amount: parseFloat(amount),
                    date: selectedDate
                }));
            
            if (paymentsToSave.length === 0) {
                toast.warning('Введите сумму оплаты хотя бы для одного сотрудника');
                return;
            }
            
            // Save each payment as an employee transaction
            let savedCount = 0;
            for (const payment of paymentsToSave) {
                try {
                    await axios.post(`${getApiUrl()}/employee-transactions`, payment, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    savedCount++;
                } catch (error) {
                    console.error('Error saving payment for employee:', payment.employee_id, error);
                }
            }
            
            if (savedCount > 0) {
                toast.success(`Сохранено ${savedCount} оплат`);
                setPaymentAmounts({}); // Clear payment inputs
                if (onDataSaved) {
                    onDataSaved();
                }
                onClose();
            } else {
                toast.error('Ошибка сохранения оплат');
            }
            
        } catch (error) {
            console.error('Error saving payments:', error);
            toast.error('Ошибка сохранения оплат');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotalManualProduction = () => {
        return Object.values(manualProductionAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
    };

    const calculateTotalDistribution = () => {
        if (!employeeAttendance || typeof employeeAttendance !== 'object') {
            return 0;
        }
        return Object.entries(employeeAttendance).reduce((sum, [employeeIdKey, attendance]) => {
            if (!attendance || !attendance.isPresent) return sum;
            // Find employee by matching id (handle both string and number)
            const employee = salaryData?.find(emp => 
                emp.id?.toString() === employeeIdKey || 
                emp.id === parseInt(employeeIdKey) ||
                emp.id === employeeIdKey
            );
            if (!employee) return sum;
            const dailyRate = getDailyRate(employee);
            const shiftMultiplier = attendance.shiftMultiplier || '1.0';
            return sum + Math.round(dailyRate * parseFloat(shiftMultiplier));
        }, 0);
    };

    const calculateTotalProductionEarnings = () => {
        if (!distributionData?.productionEarnings) {
            return 0;
        }
        return distributionData.productionEarnings;
    };

    // Calculate total salary by schedule (attendance amounts)
    const calculateTotalScheduleSalary = () => {
        if (!employeeAttendance || typeof employeeAttendance !== 'object') {
            return 0;
        }
        return Object.entries(employeeAttendance).reduce((sum, [employeeIdKey, attendance]) => {
            if (!attendance || !attendance.isPresent) return sum;
            const employee = salaryData?.find(emp => 
                emp.id?.toString() === employeeIdKey || 
                emp.id === parseInt(employeeIdKey) ||
                emp.id === employeeIdKey
            );
            if (!employee) return sum;
            const dailyRate = getDailyRate(employee);
            const shiftMultiplier = attendance.shiftMultiplier || '1.0';
            return sum + Math.round(dailyRate * parseFloat(shiftMultiplier));
        }, 0);
    };

    // Calculate total production expenses (production amounts)
    const calculateTotalProductionExpenses = () => {
        return Object.values(manualProductionAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
    };

    // Calculate total payable (attendance + production)
    const calculateTotalPayable = () => {
        return calculateTotalScheduleSalary() + calculateTotalProductionExpenses();
    };

    // Calculate distributed amount based on mode
    // In ОБЪЕМНЫЙ mode: only production amounts
    // In График mode: only attendance amounts
    const calculateDistributedAmount = () => {
        if (distributionMode === 'production') {
            // ОБЪЕМНЫЙ mode: only production amounts
            return calculateTotalProductionExpenses();
        } else {
            // График mode: only attendance amounts
            return calculateTotalScheduleSalary();
        }
    };

    // Calculate expected sum (if all employees were present with base rates)
    // Based on each employee's monthly base rate and firm's working days per month
    const calculateExpectedSum = () => {
        if (!salaryData || !Array.isArray(salaryData)) return 0;
        const workingDaysPerMonth = currentFirmData?.working_days_per_month || 26;
        return salaryData.reduce((sum, employee) => {
            // Use monthly_base_rate if available, otherwise fall back to salary field
            const monthlyBaseRate = employee.monthly_base_rate || employee.salary || 0;
            if (monthlyBaseRate <= 0) return sum;
            // Calculate daily rate: monthly_base_rate / working_days_per_month
            const dailyRate = Math.round(monthlyBaseRate / workingDaysPerMonth);
            // Assume full shift (1.0) for expected
            return sum + dailyRate;
        }, 0);
    };

    // Calculate checked attendance amount sum (only for checked employees)
    const calculateCheckedAttendanceSum = () => {
        // This is the same as calculateTotalScheduleSalary for График mode
        return calculateTotalScheduleSalary();
    };

    // Filter employees based on selected filter
    const getFilteredEmployees = () => {
        if (!salaryData || !Array.isArray(salaryData)) return [];
        
        switch (employeeFilter) {
            case 'fixed':
                return salaryData.filter(employee => {
                    // Match exact backend enum value 'fixed'
                    const type = employee.employee_type || 'volume';
                    return type === 'fixed';
                });
            case 'volume':
                return salaryData.filter(employee => {
                    // Match exact backend enum value 'volume'
                    const type = employee.employee_type || 'volume';
                    return type === 'volume';
                });
            case 'all':
            default:
                return salaryData;
        }
    };

    if (!isOpen) return null;

    // Safety check for required props
    if (!selectedDate || !selectedFirm) {
        console.error('DailyDistributionModal: Missing required props');
        return null;
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="distribution-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="header-left">
                        <h3>Распределение наличных - {moment(selectedDate).format('DD.MM.YYYY')}</h3>
                        
                        {/* Distribution Mode Selection and Filter in same row */}
                        <div className="header-controls-row">
                            <div className="distribution-mode-selector">
                                <div className="mode-buttons">
                                    <button 
                                        className={`mode-btn ${distributionMode === 'attendance' ? 'active' : ''}`}
                                        onClick={() => setDistributionMode('attendance')}
                                    >
                                        📅 График
                                    </button>
                                    <button 
                                        className={`mode-btn ${distributionMode === 'production' ? 'active' : ''}`}
                                        onClick={() => setDistributionMode('production')}
                                    >
                                        📊 ОБЪЕМНЫЙ
                                    </button>
                                    <button 
                                        className={`mode-btn ${distributionMode === 'payment' ? 'active' : ''}`}
                                        onClick={() => setDistributionMode('payment')}
                                    >
                                        💰 Оплата
                                    </button>
                                </div>
                            </div>
                            
                            {/* Employee Filter */}
                            <div className="employee-filter">
                                <label>Фильтр:</label>
                                <select 
                                    value={employeeFilter} 
                                    onChange={(e) => setEmployeeFilter(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">Все</option>
                                    <option value="fixed">👤 Фиксированные</option>
                                    <option value="volume">⭐ Объемные</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Summary in Header */}
                        <div className="production-summary-header">
                            <h4>
                                {distributionMode === 'production' ? 'Производственные доходы' : 
                                 distributionMode === 'payment' ? '💰 Оплата сотрудникам' : 
                                 'Расходы по графику'}
                            </h4>
                            <div className="earnings-info">
                                {distributionMode === 'attendance' && (
                                    <>
                                        <div className="earnings-row">
                                            <span>Ожидаемая сумма: </span>
                                            <strong>{formatCurrency(calculateExpectedSum())}</strong>
                                        </div>
                                        <div className="earnings-row">
                                            <span>Сумма по отмеченным: </span>
                                            <strong>{formatCurrency(calculateCheckedAttendanceSum())}</strong>
                                        </div>
                                        <div className="earnings-row">
                                            <span>Не отмеченные: </span>
                                            <strong 
                                                style={{
                                                    color: (calculateExpectedSum() - calculateCheckedAttendanceSum()) > 0 ? '#dc3545' : 'inherit'
                                                }}
                                            >
                                                {formatCurrency(calculateExpectedSum() - calculateCheckedAttendanceSum())}
                                            </strong>
                                        </div>
                                    </>
                                )}
                                {distributionMode === 'production' && (
                                    <>
                                        <div className="earnings-row">
                                            <span>Общая сумма к распределению: </span>
                                            <strong>{formatCurrency(calculateTotalProductionEarnings())}</strong>
                                        </div>
                                        <div className="earnings-row">
                                            <span>Распределенная сумма: </span>
                                            <strong>{formatCurrency(calculateDistributedAmount())}</strong>
                                        </div>
                                        <div className="earnings-row">
                                            <span>Остаточная сумма: </span>
                                            <strong 
                                                style={{
                                                    color: (calculateTotalProductionEarnings() - calculateDistributedAmount()) > 0 ? '#dc3545' : 'inherit'
                                                }}
                                            >
                                                {formatCurrency(calculateTotalProductionEarnings() - calculateDistributedAmount())}
                                            </strong>
                                        </div>
                                    </>
                                )}
                                {distributionMode === 'payment' && (
                                    <>
                                        <div className="earnings-row">
                                            <span>Сумма к оплате: </span>
                                            <strong style={{ color: '#28a745' }}>{formatCurrency(calculateTotalPayments())}</strong>
                                        </div>
                                        <div className="earnings-row">
                                            <span>Кол-во сотрудников: </span>
                                            <strong>{Object.values(paymentAmounts).filter(v => parseFloat(v) > 0).length}</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Загрузка данных...</p>
                        </div>
                    ) : (
                        <>
                            {/* Employee Attendance List */}
                            <div className="employee-attendance-list">
                                <h4>
                                    {distributionMode === 'attendance' ? 'Присутствие сотрудников (График)' : 
                                     distributionMode === 'production' ? 'Распределение по объему (ОБЪЕМНЫЙ)' :
                                     'Оплата сотрудникам (💰)'}
                                </h4>
                                <div className="attendance-table">
                                    <div className={`attendance-header ${distributionMode === 'production' ? 'production-mode' : ''} ${distributionMode === 'payment' ? 'payment-mode' : ''}`}>
                                        <div className="col-employee">Сотрудник</div>
                                        {distributionMode === 'attendance' && (
                                            <>
                                                <div className="col-present">Присутствует</div>
                                                <div className="col-shift">Смена</div>
                                                <div className="col-rate">Ставка (день/мес)</div>
                                                <div className="col-amount">Сумма</div>
                                            </>
                                        )}
                                        {distributionMode === 'production' && (
                                            <>
                                                <div className="col-present">Выбрать</div>
                                                <div className="col-production">Производство</div>
                                            </>
                                        )}
                                        {distributionMode === 'payment' && (
                                            <>
                                                <div className="col-balance">Остаток</div>
                                                <div className="col-payment">Сумма оплаты</div>
                                            </>
                                        )}
                                    </div>
                                    
                                    {(() => {
                                        const filteredEmployees = getFilteredEmployees();
                                        return filteredEmployees.length > 0 ? filteredEmployees.map(employee => {
                                        // Use string key for consistent lookup
                                        const employeeIdKey = employee.id?.toString();
                                        const attendance = employeeAttendance[employeeIdKey] || { 
                                            isPresent: false, 
                                            shiftMultiplier: '1.0',
                                            distributedAmount: 0 
                                        };
                                        
                                        // Ensure shiftMultiplier is always a string
                                        const shiftMultiplier = attendance.shiftMultiplier || '1.0';
                                        
                                        // Safety check for employee data
                                        if (!employee || !employee.id) {
                                            return null;
                                        }
                                        const dailyRate = getDailyRate(employee);
                                        const baseAmount = attendance.isPresent ? 
                                            Math.round((dailyRate || 0) * parseFloat(shiftMultiplier)) : 0;
                                        
                                        // Get manually entered production amount for ОБЪЕМНЫЙ mode
                                        // Use employeeIdKey to ensure consistent lookup
                                        const productionAmount = distributionMode === 'production' 
                                            ? (manualProductionAmounts[employeeIdKey] || 0) 
                                            : 0;
                                        
                                        const totalAmount = baseAmount + productionAmount;
                                        
                                        // Debug logging
                                        if (employee.id === 167) { // Алижон 1
                                            console.log('Debug for Алижон 1:', {
                                                employee: employee.employee,
                                                salary: employee.salary,
                                                isPresent: attendance.isPresent,
                                                shiftMultiplier: shiftMultiplier,
                                                dailyRate: dailyRate,
                                                baseAmount: baseAmount,
                                                productionAmount: productionAmount,
                                                totalAmount: totalAmount,
                                                attendance: attendance
                                            });
                                        }
                                        

                                        return (
                                            <div key={`${employee.id}-${attendance.isPresent}-${attendance.shiftMultiplier}`} className={`attendance-row ${distributionMode === 'production' ? 'production-mode' : ''} ${distributionMode === 'payment' ? 'payment-mode' : ''}`}>
                                                <div className="col-employee">
                                                    <span className="employee-icon">{employee.icon || '👤'}</span>
                                                    {employee.employee || 'Неизвестный сотрудник'}
                                                </div>
                                                {distributionMode === 'attendance' && (
                                                    <>
                                                        <div className="col-present">
                                                            <label className="modern-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={attendance.isPresent || false}
                                                                    onChange={(e) => updateEmployeeAttendance(employee.id, {
                                                                        isPresent: e.target.checked,
                                                                        shiftMultiplier: e.target.checked ? (attendance.shiftMultiplier || '1.0') : '1.0'
                                                                    })}
                                                                />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-shift">
                                                            <select
                                                                value={shiftMultiplier}
                                                                onChange={(e) => updateEmployeeAttendance(employee.id, {
                                                                    shiftMultiplier: e.target.value
                                                                })}
                                                                disabled={!attendance.isPresent}
                                                                className="shift-select"
                                                            >
                                                                <option value="0.5">0.5 (Половина)</option>
                                                                <option value="1.0">1.0 (Полная)</option>
                                                                <option value="1.5">1.5 (Полтора)</option>
                                                                <option value="2.0">2.0 (Двойная)</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-rate">
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                {formatCurrency(dailyRate || 0)} / {formatCurrency(employee.salary || 0)}
                                                            </div>
                                                        </div>
                                                        <div className="col-amount">
                                                            <strong className={attendance.isPresent ? 'text-success' : 'text-muted'}>
                                                                {formatCurrency(baseAmount || 0)}
                                                            </strong>
                                                        </div>
                                                    </>
                                                )}
                                                {distributionMode === 'production' && (
                                                    <>
                                                        <div className="col-present">
                                                            <label className="modern-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={attendance.isPresent || false}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        updateEmployeeAttendance(employee.id, {
                                                                            isPresent: isChecked,
                                                                            shiftMultiplier: attendance.shiftMultiplier || '1.0'
                                                                        });
                                                                        // Clear production amount if unchecked
                                                                        if (!isChecked) {
                                                                            updateManualProductionAmount(employeeIdKey, 0);
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-production">
                                                            <input
                                                                type="text"
                                                                value={manualProductionAmounts[employeeIdKey] 
                                                                    ? formatCurrency(parseFloat(manualProductionAmounts[employeeIdKey]) || 0)
                                                                    : ''}
                                                                onChange={(e) => {
                                                                    // Remove commas and non-numeric characters except decimal point
                                                                    const rawValue = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                                                                    const numericValue = parseFloat(rawValue) || 0;
                                                                    updateManualProductionAmount(employeeIdKey, numericValue);
                                                                }}
                                                                onBlur={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                                                                    const value = parseFloat(rawValue) || 0;
                                                                    updateManualProductionAmount(employeeIdKey, value);
                                                                }}
                                                                disabled={!attendance.isPresent}
                                                                className="production-amount-input"
                                                                placeholder="0"
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 8px',
                                                                    border: '1px solid #dee2e6',
                                                                    borderRadius: '4px',
                                                                    fontSize: '17.5px',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500'
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {distributionMode === 'payment' && (
                                                    <>
                                                        <div className="col-balance">
                                                            <strong className={employee.currentRemainder > 0 ? 'text-success' : employee.currentRemainder < 0 ? 'text-danger' : ''}>
                                                                {formatCurrency(employee.currentRemainder || 0)}
                                                            </strong>
                                                        </div>
                                                        <div className="col-payment">
                                                            <input
                                                                type="text"
                                                                value={paymentAmounts[employeeIdKey] 
                                                                    ? formatCurrency(parseFloat(paymentAmounts[employeeIdKey]) || 0)
                                                                    : ''}
                                                                onChange={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                                                                    const numericValue = parseFloat(rawValue) || 0;
                                                                    updatePaymentAmount(employeeIdKey, numericValue);
                                                                }}
                                                                onBlur={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                                                                    const value = parseFloat(rawValue) || 0;
                                                                    updatePaymentAmount(employeeIdKey, value);
                                                                }}
                                                                className="payment-amount-input"
                                                                placeholder="0"
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 8px',
                                                                    border: '1px solid #28a745',
                                                                    borderRadius: '4px',
                                                                    fontSize: '17.5px',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500',
                                                                    backgroundColor: '#f8fff8'
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                        }) : (
                                            <div className="no-data">Нет данных о сотрудниках</div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Отмена
                    </button>
                    {distributionMode === 'payment' ? (
                        <button 
                            className="btn btn-success"
                            onClick={handleSavePayments}
                            disabled={loading}
                        >
                            {loading ? 'Сохранение...' : `💰 Сохранить оплаты (${formatCurrency(calculateTotalPayments())})`}
                        </button>
                    ) : (
                        <button 
                            className="btn btn-primary"
                            onClick={handleSaveDistribution}
                            disabled={loading}
                        >
                            {loading ? 'Сохранение...' : 'Сохранить распределение'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyDistributionModal;
